import { fetchHealthJourneys } from '../healthJourney';
import { fetchNotifications } from '../notifications';
import { RootState } from '../store';
import { createAppAsyncThunk } from '../store/extensions';
import { selectStoryTopics, Topic } from '../stories';
import { EmailsDto, UpdateMemberDto, UpdateUserDto } from './dtos';
import { MemberPreference, Membership, PreferenceType } from './model';
import { VerifyEmailDto } from './model/VerifyEmailDto';
import {
  acceptMemberRequestCall,
  blockMemberCall,
  fetchJourneyRelationshipsCall,
  fetchPreferencesCall,
  fetchTopicsPreferencesCall,
  getAvatarsCall,
  getBlockedMembersCall,
  getCarepointsCall,
  getCarepointsRewardsCall,
  getCountriesCall,
  getMemberCall,
  getMemberInfoCall,
  getMembershipCall,
  getReferralLinkCall,
  getUserCall,
  insertFollowingMemberCall,
  registerByCampaignLinkCall,
  registerByReferralLinkCall,
  rejectMemberRequestCall,
  removeFollowingMemberCall,
  reportMemberCall,
  resendVerificationEmailLinkCall,
  sendEmailsInviteCall,
  unblockMemberCall,
  updateMemberCall,
  updateMembershipCall,
  updatePreferencesCall,
  updateTopicsPreferencesCall,
  updateUserCall,
  verifyEmailCall,
} from './service';

export const fetchUser = createAppAsyncThunk('user/getUser', () => {
  return getUserCall();
});

export const refreshUserAfterProfileOnboarding = createAppAsyncThunk(
  'user/refreshAfterProfileOnboarding',
  (_memberId: string) => getUserCall()
);

export const updateUser = createAppAsyncThunk(
  'user/updateUser',
  async (user: UpdateUserDto, { dispatch }) => {
    const updatedUser = await updateUserCall(user);
    dispatch(fetchHealthJourneys());
    return updatedUser;
  }
);

export const fetchMember = createAppAsyncThunk('member/getMember', () => {
  return getMemberCall();
});

export const fetchMembership = createAppAsyncThunk(
  'member/getMembership',
  () => {
    return getMembershipCall();
  }
);

export const updateMember = createAppAsyncThunk(
  'user/updateMember',
  (member: UpdateMemberDto) => {
    return updateMemberCall(member);
  }
);

export const updateMemberbership = createAppAsyncThunk(
  'member/updateMemberbership',
  (data: Membership) => {
    return updateMembershipCall(data);
  }
);

export const fetchCountries = createAppAsyncThunk(
  'profile/getCountries',
  () => {
    return getCountriesCall();
  }
);

export const fetchCarepoints = createAppAsyncThunk(
  'profile/getCarepoints',
  () => {
    return getCarepointsCall();
  }
);

export const fetchCarepointsRewards = createAppAsyncThunk(
  'profile/getCarepointsRewards',
  () => {
    return getCarepointsRewardsCall();
  }
);

export const fetchAvatars = createAppAsyncThunk('profile/getAvatars', () => {
  return getAvatarsCall();
});

export const getMemberInfo = createAppAsyncThunk(
  'member/getInfo',
  (arg: { memberId: string; roomId?: string }) => {
    return getMemberInfoCall(arg.memberId, arg.roomId);
  }
);

export const blockMember = createAppAsyncThunk(
  'member/block',
  (arg: { memberId: string; roomId?: string }) => {
    return blockMemberCall(arg.memberId, arg.roomId);
  }
);

export const unblockMember = createAppAsyncThunk(
  'member/unblock',
  (arg: { memberId: string; roomId?: string }) => {
    return unblockMemberCall(arg.memberId, arg.roomId);
  }
);

export const followAuthor = createAppAsyncThunk(
  'stories/followAuthor',
  async (args: { memberId: string }) => {
    return insertFollowingMemberCall(args.memberId);
  }
);

export const unfollowAuthor = createAppAsyncThunk(
  'stories/unfollowAuthor',
  async (args: { memberId: string }) => {
    return removeFollowingMemberCall(args.memberId);
  }
);

export const getAuthorInfo = createAppAsyncThunk(
  'member/getAuthorInfo',
  (args: { memberId: string; roomId?: string }) => {
    return getMemberInfoCall(args.memberId, args.roomId);
  }
);

export const fetchMyPreferredTopics = createAppAsyncThunk(
  'member/getPreferences',
  () => {
    return fetchTopicsPreferencesCall(PreferenceType.topics);
  }
);

export const fetchJourneyRelationships = createAppAsyncThunk(
  'member/relationshipTypes',
  () => {
    return fetchJourneyRelationshipsCall();
  }
);

export const updateMyPreferredTopics = createAppAsyncThunk(
  'member/updateMyPreferredTopics',
  (args: MemberPreference, { getState }) => {
    const state = getState() as RootState;
    const possibleTopics: Topic[] = selectStoryTopics(state);
    const filteredTopics = [
      ...possibleTopics
        .filter((topic) =>
          args.values.some(
            (memberPreference) => memberPreference.value === topic.originalTopic
          )
        )
        .map((topic) => {
          return { value: topic.originalTopic };
        }),
    ];
    return updateTopicsPreferencesCall({
      type: args.type,
      values: filteredTopics,
    });
  }
);

export const reportMember = createAppAsyncThunk(
  'member/report',
  (arg: { memberId: string; roomId?: string }) => {
    return reportMemberCall(arg.memberId, arg.roomId);
  }
);

export const getBlockedMembers = createAppAsyncThunk(
  'member/blockedMembers/get',
  (numrecs?: number) => {
    return getBlockedMembersCall(numrecs);
  }
);

export const fetchReferralLink = createAppAsyncThunk(
  'profile/referralLink/get',
  () => {
    return getReferralLinkCall();
  }
);

export const sendEmailsInvite = createAppAsyncThunk(
  'profile/sendInvitation/post',
  (emails: EmailsDto) => {
    return sendEmailsInviteCall(emails);
  }
);

export const registerByReferralLink = createAppAsyncThunk(
  '/referral/registered',
  (referralId: string) => {
    return registerByReferralLinkCall(referralId);
  }
);

export const registerByCampaignLink = createAppAsyncThunk(
  '/campaign/registered',
  (campaignId: string) => {
    return registerByCampaignLinkCall(campaignId);
  }
);

export const fetchMyPreferences = createAppAsyncThunk(
  'member/getPreferences/notifications',
  () => {
    return fetchPreferencesCall();
  }
);

export const updateMyPreferences = createAppAsyncThunk(
  'member/updatePreferences',
  (args: { memberPreferences: MemberPreference[] }) => {
    return updatePreferencesCall(args.memberPreferences);
  }
);

export const resendVerificationEmailLink = createAppAsyncThunk(
  'member/resendVerificationLink',
  () => {
    return resendVerificationEmailLinkCall();
  }
);

export const verifyEmail = createAppAsyncThunk(
  'member/verificationLink',
  (args: VerifyEmailDto) => {
    return verifyEmailCall(args);
  }
);

export const rejectMemberRequest = createAppAsyncThunk(
  'member/request/reject',
  async (arg: { memberId: string; roomId?: string }, { dispatch }) => {
    const response = await rejectMemberRequestCall(arg.memberId, arg.roomId);
    dispatch(fetchNotifications({}));
    return response;
  }
);

export const acceptMemberRequest = createAppAsyncThunk(
  'member/request/accept',
  async (arg: { memberId: string; roomId?: string }, { dispatch }) => {
    const response = await acceptMemberRequestCall(arg.memberId, arg.roomId);
    dispatch(fetchNotifications({}));
    return response;
  }
);
